import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Tooltip } from '../../../components/ui/tooltip';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { CompletionEmailNotifyBlock, isValidNotifyEmail } from './CompletionEmailNotifyBlock';
import {
  convertLengthFromFt,
  convertLengthToFt,
} from '../../../utils/unitUtils';

export type SequenceGenerationMode = 'new' | 'regenerate' | 'append';

/** Which sequence-type radios to show (entry-point specific). */
export type SequenceModeOptions = 'all' | 'new-regenerate' | 'none' | 'append-only';

/** Project pipe setup: nominal length (ft) and OD (mm) for sequence forms. */
export interface PipeSpecificationForSequence {
  nominalPipeLengthFt: number;
  nominalODMm: number;
  /** From setup Target Number of Pipes when set. */
  targetNumberOfPipes?: number;
  /** From setup Target Pipeline Length (ft) when set. */
  targetPipelineLengthFt?: number;
  /** From setup 6 o'clock Exclusion (deg). */
  exclusion6OClockDeg?: number;
  /** From setup Weld Seam Exclusion (deg). */
  weldSeamExclusionDeg?: number;
  /** From setup HiLo Threshold (mm). */
  hiloThresholdMm?: number;
}

export interface GenerateSequenceFormValues {
  mode: SequenceGenerationMode;
  sequenceName?: string;
  nominalPipeLengthFt: number;
  targetNumberOfPipes: number;
  targetPipelineLengthFt: number;
  nominalODMm: number;
  exclusion6OClockMm: number;
  weldSeamExclusionMm: number;
  hiloThresholdMm: number;
  pipeMaterial: string;
  pipeType: 'single' | 'double';
  pipeRotation: 'any' | 'clockwise' | 'antiClockwise';
  fitQuality: 'optimal' | 'acceptable';
  allowPipeTranslation: boolean;
}

/** Payload passed to onSubmit for pipe-based generate API (parent adds projectNumber, pipeIds). */
export interface PipeBasedGenerateSubmitValues {
  mode: SequenceGenerationMode;
  sequenceName: string;
  existingSequenceRef?: { spreadId?: string; subSpreadId?: string };
  /** Optional; merged with notifyEmails server-side */
  notifyEmail?: string;
  notifyEmails?: string[];
  config: {
    nominalPipeLengthFt?: number;
    targetNumberOfPipes: number;
    targetPipelineLengthFt?: number;
    nominalODmm?: number;
    /** Degrees; key name retained for API compatibility. */
    exclusion6OClockMm?: number;
    /** Degrees; key name retained for API compatibility. */
    weldSeamExclusionMm?: number;
    hiLoThresholdMm?: number;
    pipeMaterial?: string;
    pipeType?: string;
    pipeRotation?: string;
    fitQuality?: string;
    allowPipeTranslation?: boolean;
  };
}

type NumericFieldKey =
  | 'nominalPipeLengthFt'
  | 'targetNumberOfPipes'
  | 'targetPipelineLengthFt'
  | 'nominalODMm'
  | 'exclusion6OClockMm'
  | 'weldSeamExclusionMm'
  | 'hiloThresholdMm';

type FormState = {
  [K in keyof GenerateSequenceFormValues]: GenerateSequenceFormValues[K] extends number
    ? string
    : GenerateSequenceFormValues[K];
} & { existingSequenceValue?: string };

type FormErrors = Partial<Record<NumericFieldKey, string>>;

export interface SequenceOptionItem {
  label: string;
  value: string;
  sequenceName: string;
}

export interface PipeSelectionItem {
  id: string;
  label: string;
  subLabel?: string;
  sequence?: string;
  status?: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface GenerateSequenceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: PipeBasedGenerateSubmitValues) => void;
  isSubmitting?: boolean;
  isAutofillLoading?: boolean;
  initialValues?: Partial<GenerateSequenceFormValues>;
  selectedPipeCount?: number;
  pipeIds?: string[];
  sequences?: SequenceOptionItem[];
  title?: string;
  submitLabel?: string;
  modeOverride?: SequenceGenerationMode;
  lockModeSelection?: boolean;
  existingSequenceValue?: string;
  /** @deprecated Layout is fullscreen by default; optional extra classes only. */
  dialogClassName?: string;
  /** When set, sync nominal length / OD from project pipe size; still merges with selection-derived counts. */
  pipeSpecification?: PipeSpecificationForSequence | null;
  /** When true, target pipe count and pipeline length follow `pipeIds` / `selectedPipeIds`. */
  syncMetricsFromPipeSelection?: boolean;
  /** When true, applying `initialValues` when they change while the dialog stays open (e.g. async prefill). */
  applyInitialValuesWhileOpen?: boolean;
  pipeSelectionItems?: PipeSelectionItem[];
  selectedPipeIds?: string[];
  onSelectedPipeIdsChange?: (pipeIds: string[]) => void;
  simplePipeSelectionTable?: boolean;
  /** Optional controls above the pipe list (e.g. goes-in % filter for random-compat). */
  pipeSelectionToolbar?: React.ReactNode;
  /** default `all` — `new-regenerate` hides Append; `append-only` shows Append selected and disabled (Update Sequence); `none` hides radios (legacy). */
  sequenceModeOptions?: SequenceModeOptions;
}

const FIELD_TOOLTIPS: Record<string, string> = {
  nominalPipeLengthFt: 'Nominal length of the pipe in feet.',
  targetNumberOfPipes: 'The expected number of pipes to be used in this project.',
  targetPipelineLengthFt:
    'The expected length of the pipeline. This parameter is automatically calculated based on the nominal pipe length and the target number of pipes.',
  nominalODMm: 'The nominal outer diameter of the pipe in millimeters.',
  exclusion6OClockMm:
    'Angular exclusion at the six o\'clock position, in degrees (0–90).',
  weldSeamExclusionMm:
    'Angular exclusion around the weld seam, in degrees.',
  hiloThresholdMm:
    'The maximum radial difference of two pipes (in millimeters).',
  pipeMaterial: 'Material of the pipe.',
  pipeType: 'The type of pipe used for the build process.',
  pipeRotation:
    'The direction in which the pipe may be rotated while building the pipeline.',
  fitQuality:
    'Select alignment mode: find the best rotation (optimal) or use the first rotation that works (acceptable).',
  allowPipeTranslation:
    'Allow reversing the pipe direction (A → B or B → A) during pipeline creation.',
};

const LENGTH_UNITS = ['ft', 'm'] as const;
type LengthUnit = (typeof LENGTH_UNITS)[number];

const LENGTH_UNIT_LABELS: Record<LengthUnit, string> = { ft: 'feet', m: 'meter' };
const MM_UNIT_LABELS = { mm: 'millimeters' } as const;

const DEFAULT_VALUES: GenerateSequenceFormValues = {
  mode: 'new',
  sequenceName: '',
  nominalPipeLengthFt: 40,
  targetNumberOfPipes: 24,
  targetPipelineLengthFt: 960,
  nominalODMm: 508,
  exclusion6OClockMm: 36,
  weldSeamExclusionMm: 18,
  hiloThresholdMm: 1.5,
  pipeMaterial: 'Iron',
  pipeType: 'single',
  pipeRotation: 'any',
  fitQuality: 'optimal',
  allowPipeTranslation: true,
};

export const GenerateSequenceModal: React.FC<GenerateSequenceModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  isAutofillLoading = false,
  initialValues,
  selectedPipeCount,
  pipeIds = [],
  sequences = [],
  title = 'Generate Sequence',
  submitLabel = 'Generate',
  modeOverride,
  lockModeSelection = false,
  existingSequenceValue,
  dialogClassName,
  pipeSpecification = null,
  syncMetricsFromPipeSelection = false,
  applyInitialValuesWhileOpen = false,
  pipeSelectionItems = [],
  selectedPipeIds,
  onSelectedPipeIdsChange,
  simplePipeSelectionTable = false,
  pipeSelectionToolbar,
  sequenceModeOptions = 'all',
}) => {
  const authUserEmail = useSelector((state: { auth?: { user?: { email?: string } } }) =>
    state.auth?.user?.email?.trim() ?? ''
  );

  const [notifyByEmail, setNotifyByEmail] = useState(true);
  const [notifyEmails, setNotifyEmails] = useState<string[]>([]);
  const [newNotifyEmail, setNewNotifyEmail] = useState('');
  const [notifyEmailError, setNotifyEmailError] = useState('');

  const mergedInitialValues: GenerateSequenceFormValues = useMemo(
    () => ({
      ...DEFAULT_VALUES,
      ...initialValues,
    }),
    [initialValues]
  );

  const [sequenceType, setSequenceType] = useState<SequenceGenerationMode>(mergedInitialValues.mode);
  const [formState, setFormState] = useState<FormState>({
    mode: mergedInitialValues.mode,
    sequenceName: mergedInitialValues.sequenceName ?? '',
    nominalPipeLengthFt: mergedInitialValues.nominalPipeLengthFt.toString(),
    targetNumberOfPipes: mergedInitialValues.targetNumberOfPipes.toString(),
    targetPipelineLengthFt: mergedInitialValues.targetPipelineLengthFt.toString(),
    nominalODMm: mergedInitialValues.nominalODMm.toString(),
    exclusion6OClockMm: mergedInitialValues.exclusion6OClockMm.toString(),
    weldSeamExclusionMm: mergedInitialValues.weldSeamExclusionMm.toString(),
    hiloThresholdMm: mergedInitialValues.hiloThresholdMm.toString(),
    pipeMaterial: mergedInitialValues.pipeMaterial,
    pipeType: mergedInitialValues.pipeType,
    pipeRotation: mergedInitialValues.pipeRotation,
    fitQuality: mergedInitialValues.fitQuality,
    allowPipeTranslation: mergedInitialValues.allowPipeTranslation,
    existingSequenceValue: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [sequenceError, setSequenceError] = useState<string | undefined>(undefined);

  const [nominalPipeLengthUnit, setNominalPipeLengthUnit] = useState<LengthUnit>('ft');
  const [targetPipelineLengthUnit, setTargetPipelineLengthUnit] = useState<LengthUnit>('ft');
  const [pipeSearchText, setPipeSearchText] = useState('');
  const [pipeSequenceFilter, setPipeSequenceFilter] = useState('all');
  const [pipeStatusFilter, setPipeStatusFilter] = useState('all');

  const prevOpenRef = useRef(false);
  const lastAppliedPrefillRef = useRef<string | null>(null);

  const prefillSnapshot = useMemo(() => JSON.stringify(mergedInitialValues), [mergedInitialValues]);
  const pipeSpecKey = useMemo(
    () => (pipeSpecification ? JSON.stringify(pipeSpecification) : ''),
    [pipeSpecification]
  );

  // Apply initial / prefill values: on each open, and while open when snapshot changes if allowed (async prefill).
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      lastAppliedPrefillRef.current = null;
      return;
    }

    const firstOpen = !prevOpenRef.current;
    prevOpenRef.current = true;

    if (applyInitialValuesWhileOpen) {
      if (lastAppliedPrefillRef.current === prefillSnapshot) return;
      lastAppliedPrefillRef.current = prefillSnapshot;
    } else if (!firstOpen) {
      return;
    } else {
      lastAppliedPrefillRef.current = prefillSnapshot;
    }

    setSequenceType(mergedInitialValues.mode);
    setFormState((prev) => ({
      mode: mergedInitialValues.mode,
      sequenceName: mergedInitialValues.sequenceName ?? '',
      nominalPipeLengthFt: mergedInitialValues.nominalPipeLengthFt.toString(),
      targetNumberOfPipes: mergedInitialValues.targetNumberOfPipes.toString(),
      targetPipelineLengthFt: mergedInitialValues.targetPipelineLengthFt.toString(),
      nominalODMm: mergedInitialValues.nominalODMm.toString(),
      exclusion6OClockMm: mergedInitialValues.exclusion6OClockMm.toString(),
      weldSeamExclusionMm: mergedInitialValues.weldSeamExclusionMm.toString(),
      hiloThresholdMm: mergedInitialValues.hiloThresholdMm.toString(),
      pipeMaterial: mergedInitialValues.pipeMaterial,
      pipeType: mergedInitialValues.pipeType,
      pipeRotation: mergedInitialValues.pipeRotation,
      fitQuality: mergedInitialValues.fitQuality,
      allowPipeTranslation: mergedInitialValues.allowPipeTranslation,
      existingSequenceValue: prev.existingSequenceValue ?? '',
    }));
    if (firstOpen || applyInitialValuesWhileOpen) {
      setNominalPipeLengthUnit('ft');
      setTargetPipelineLengthUnit('ft');
    }
    if (firstOpen) {
      setPipeSearchText('');
      setPipeSequenceFilter('all');
      setPipeStatusFilter('all');
      setErrors({});
      setSequenceError(undefined);
    }
  }, [open, prefillSnapshot, mergedInitialValues, applyInitialValuesWhileOpen]);

  useEffect(() => {
    if (!open || !syncMetricsFromPipeSelection) return;

    const setupTargetPipes = Number(pipeSpecification?.targetNumberOfPipes ?? 0);
    const targetPipes =
      setupTargetPipes > 0 ? setupTargetPipes : Math.max(pipeIds.length, 1);

    setFormState((prev) => {
      const lenFt = pipeSpecification
        ? pipeSpecification.nominalPipeLengthFt
        : convertLengthToFt(
            Number(prev.nominalPipeLengthFt) || DEFAULT_VALUES.nominalPipeLengthFt,
            nominalPipeLengthUnit
          );
      const setupPipelineFt = Number(pipeSpecification?.targetPipelineLengthFt ?? 0);
      const pipelineFt = setupPipelineFt > 0 ? setupPipelineFt : targetPipes * lenFt;
      const pipelineStr = pipeSpecification
        ? String(pipelineFt)
        : String(convertLengthFromFt(pipelineFt, targetPipelineLengthUnit));

      const next = {
        ...prev,
        nominalPipeLengthFt: pipeSpecification
          ? String(pipeSpecification.nominalPipeLengthFt)
          : prev.nominalPipeLengthFt,
        targetNumberOfPipes: String(targetPipes),
        targetPipelineLengthFt: pipelineStr,
        nominalODMm: pipeSpecification ? String(pipeSpecification.nominalODMm) : prev.nominalODMm,
      };

      if (pipeSpecification?.exclusion6OClockDeg != null) {
        next.exclusion6OClockMm = String(pipeSpecification.exclusion6OClockDeg);
      }
      if (pipeSpecification?.weldSeamExclusionDeg != null) {
        next.weldSeamExclusionMm = String(pipeSpecification.weldSeamExclusionDeg);
      }
      if (pipeSpecification?.hiloThresholdMm != null) {
        next.hiloThresholdMm = String(pipeSpecification.hiloThresholdMm);
      }

      return next;
    });

    if (pipeSpecification) {
      setNominalPipeLengthUnit('ft');
      setTargetPipelineLengthUnit('ft');
    }
  }, [open, syncMetricsFromPipeSelection, pipeIds.join(','), pipeSpecKey]);

  useEffect(() => {
    if (!open || !modeOverride) return;
    setSequenceType(modeOverride);
    setFormState((prev) => ({ ...prev, mode: modeOverride }));
  }, [open, modeOverride]);

  useEffect(() => {
    if (!open || !existingSequenceValue || !modeOverride || modeOverride === 'new') return;
    setFormState((prev) => ({ ...prev, existingSequenceValue }));
  }, [open, existingSequenceValue, modeOverride]);

  const metricsPills = useMemo(() => {
    const nPipes = formState.targetNumberOfPipes;
    return [
      {
        label: 'Nominal Pipe Length',
        text: `${formState.nominalPipeLengthFt} ${LENGTH_UNIT_LABELS[nominalPipeLengthUnit]}`,
        bgColor: 'bg-violet-100 text-violet-800',
      },
      {
        label: 'Target Number of Pipes',
        text: `${nPipes} pipes`,
        bgColor: 'bg-amber-100 text-amber-800',
      },
      {
        label: 'Target Pipeline Length',
        text: `${formState.targetPipelineLengthFt} ${LENGTH_UNIT_LABELS[targetPipelineLengthUnit]}`,
        bgColor: 'bg-emerald-100 text-emerald-800',
      },
      {
        label: 'Nominal OD',
        text: `${(() => {
          const n = Number(formState.nominalODMm);
          return Number.isFinite(n) ? n.toFixed(2) : formState.nominalODMm;
        })()} ${MM_UNIT_LABELS.mm}`,
        bgColor: 'bg-sky-100 text-sky-800',
      },
    ];
  }, [
    formState.nominalPipeLengthFt,
    formState.targetNumberOfPipes,
    formState.targetPipelineLengthFt,
    formState.nominalODMm,
    nominalPipeLengthUnit,
    targetPipelineLengthUnit,
  ]);

  const validateNumericField = (key: NumericFieldKey, value: string, mode: SequenceGenerationMode): string | undefined => {
    if (mode !== 'new' && (key === 'nominalPipeLengthFt' || key === 'targetNumberOfPipes' || key === 'targetPipelineLengthFt')) {
      return undefined;
    }
    if (key === 'targetNumberOfPipes') {
      if (!value || value.trim() === '') return 'Required';
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return 'Must be a number';
      if (numeric <= 0) return 'Must be a positive number';
      return undefined;
    }
    if (!value || value.trim() === '') {
      return undefined;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return 'Must be a number';
    }
    if (key === 'exclusion6OClockMm') {
      if (numeric < 0 || numeric > 90) {
        return 'Must be between 0 and 90';
      }
      return undefined;
    }
    if (numeric <= 0) {
      return 'Must be a positive number';
    }
    if (key === 'weldSeamExclusionMm' && numeric > 360) {
      return 'Value is too large (max 360 deg)';
    }
    return undefined;
  };

  const runValidation = (state: FormState): FormErrors => {
    const mode = state.mode;
    const fieldKeys: NumericFieldKey[] = [
      'nominalPipeLengthFt',
      'targetNumberOfPipes',
      'targetPipelineLengthFt',
      'nominalODMm',
      'exclusion6OClockMm',
      'weldSeamExclusionMm',
      'hiloThresholdMm',
    ];

    const nextErrors: FormErrors = {};
    fieldKeys.forEach((key) => {
      const maybeError = validateNumericField(key, state[key] as string, mode);
      if (maybeError) {
        nextErrors[key] = maybeError;
      }
    });
    return nextErrors;
  };

  const isFormValid = useMemo(() => {
    const validationResult = runValidation(formState);
    const mode = formState.mode;
    const hasSequences = sequences && sequences.length > 0;
    const requiresSequence = mode !== 'new' && hasSequences;
    const existingVal = (formState.existingSequenceValue ?? '').trim();
    const sequenceValid = !requiresSequence || existingVal.length > 0;
    const sequenceNameOk = (formState.sequenceName ?? '').trim().length > 0;
    const pipesOkForNew = mode !== 'new' || pipeIds.length >= 3;
    const pipesOkForAppend = mode !== 'append' || pipeIds.length >= 1;
    const notifyEmailsValid =
      !notifyByEmail ||
      (notifyEmails.length > 0 && notifyEmails.every((e) => isValidNotifyEmail(e)));
    return (
      Object.keys(validationResult).length === 0 &&
      sequenceValid &&
      (mode === 'new' ? sequenceNameOk && pipesOkForNew : true) &&
      (mode === 'append' ? pipesOkForAppend : true) &&
      (mode === 'regenerate' ? true : true) &&
      notifyEmailsValid &&
      !isAutofillLoading &&
      !isSubmitting
    );
  }, [formState, isAutofillLoading, isSubmitting, sequences, pipeIds.length, notifyByEmail, notifyEmails]);

  const uniquePipeSequences = useMemo(
    () => Array.from(new Set(pipeSelectionItems.map((item) => item.sequence).filter(Boolean))) as string[],
    [pipeSelectionItems]
  );
  const uniquePipeStatuses = useMemo(
    () => Array.from(new Set(pipeSelectionItems.map((item) => item.status).filter(Boolean))) as string[],
    [pipeSelectionItems]
  );
  const filteredPipeSelectionItems = useMemo(() => {
    const search = pipeSearchText.trim().toLowerCase();
    return pipeSelectionItems.filter((pipe) => {
      const matchesSearch =
        !search ||
        pipe.id.toLowerCase().includes(search) ||
        pipe.label.toLowerCase().includes(search);
      const matchesSequence =
        pipeSequenceFilter === 'all' || (pipe.sequence ?? 'unassigned') === pipeSequenceFilter;
      const matchesStatus = pipeStatusFilter === 'all' || (pipe.status ?? 'unknown') === pipeStatusFilter;
      return matchesSearch && matchesSequence && matchesStatus;
    });
  }, [pipeSelectionItems, pipeSearchText, pipeSequenceFilter, pipeStatusFilter]);
  const isUpdateLayout = pipeSelectionItems.length > 0 && Boolean(onSelectedPipeIdsChange);

  useEffect(() => {
    if (!open) return;
    if (!notifyByEmail || !authUserEmail) return;
    setNotifyEmails((prev) => (prev.includes(authUserEmail) ? prev : [...prev, authUserEmail]));
  }, [open, authUserEmail, notifyByEmail]);

  const handleAddNotifyEmail = () => {
    const t = newNotifyEmail.trim();
    if (!t) {
      setNotifyEmailError('Please enter an email address');
      return;
    }
    if (!isValidNotifyEmail(t)) {
      setNotifyEmailError('Please enter a valid email address');
      return;
    }
    if (notifyEmails.includes(t)) {
      setNotifyEmailError('Email address already exists');
      return;
    }
    setNotifyEmails((prev) => [...prev, t]);
    setNewNotifyEmail('');
    setNotifyEmailError('');
  };

  const handleRemoveNotifyEmail = (email: string) => {
    setNotifyEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleNotifyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNotifyEmail();
    }
  };

  const handleNumericChange = (key: NumericFieldKey, value: string) => {
    setFormState((prev) => {
      const next = { ...prev, [key]: value };
      setErrors((prevErrors) => {
        const cloned = { ...prevErrors };
        const maybeError = validateNumericField(key, value, next.mode);
        if (maybeError) {
          cloned[key] = maybeError;
        } else {
          delete cloned[key];
        }
        return cloned;
      });
      if (key === 'targetNumberOfPipes' || key === 'nominalPipeLengthFt') {
        const pipes = Number(next.targetNumberOfPipes);
        const lengthDisplay = Number(next.nominalPipeLengthFt);
        if (!Number.isNaN(pipes) && !Number.isNaN(lengthDisplay) && pipes > 0 && lengthDisplay > 0) {
          const lengthFt = convertLengthToFt(lengthDisplay, nominalPipeLengthUnit);
          const pipelineLengthFt = pipes * lengthFt;
          next.targetPipelineLengthFt = convertLengthFromFt(pipelineLengthFt, targetPipelineLengthUnit).toString();
          const tlError = validateNumericField('targetPipelineLengthFt', next.targetPipelineLengthFt, next.mode);
          setErrors((prevErrors) => {
            const cloned = { ...prevErrors };
            if (tlError) {
              cloned.targetPipelineLengthFt = tlError;
            } else {
              delete cloned.targetPipelineLengthFt;
            }
            return cloned;
          });
        }
      }
      return next;
    });
  };

  const handleLengthUnitChange = (
    field: 'nominalPipeLengthFt' | 'targetPipelineLengthFt',
    newUnit: LengthUnit
  ) => {
    const setUnit = field === 'nominalPipeLengthFt' ? setNominalPipeLengthUnit : setTargetPipelineLengthUnit;
    const currentUnit = field === 'nominalPipeLengthFt' ? nominalPipeLengthUnit : targetPipelineLengthUnit;
    setUnit(newUnit);
    setFormState((prev) => {
      const str = String(prev[field] ?? '').trim();
      if (str === '') return prev;
      const raw = Number(str);
      if (Number.isNaN(raw)) return prev;
      const ft = convertLengthToFt(raw, currentUnit);
      const display = convertLengthFromFt(ft, newUnit);
      return { ...prev, [field]: display.toString() };
    });
  };

  const parseExistingSequenceRef = (value: string): { spreadId?: string; subSpreadId?: string } | undefined => {
    const v = (value ?? '').trim();
    if (!v) return undefined;
    if (v.startsWith('spread:')) return { spreadId: v.slice(7) };
    if (v.startsWith('subSpread:')) return { subSpreadId: v.slice(10) };
    return undefined;
  };

  const handleSubmit = () => {
    const validationResult = runValidation(formState);
    setErrors(validationResult);
    const sequenceName = (formState.sequenceName ?? '').trim();
    const hasSequences = sequences && sequences.length > 0;
    const requiresSequence = formState.mode !== 'new' && hasSequences;
    const existingVal = (formState.existingSequenceValue ?? '').trim();
    if (!sequenceName) {
      setSequenceError('Sequence name is required');
    } else if (requiresSequence && !existingVal) {
      setSequenceError('Select an existing sequence');
    } else {
      setSequenceError(undefined);
    }

    const mode = formState.mode;
    const pipesOkForNew = mode !== 'new' || pipeIds.length >= 3;
    const pipesOkForAppend = mode !== 'append' || pipeIds.length >= 1;
    if (
      Object.keys(validationResult).length > 0 ||
      !sequenceName ||
      (requiresSequence && !existingVal) ||
      !pipesOkForNew ||
      !pipesOkForAppend
    ) {
      return;
    }

    if (notifyByEmail) {
      if (notifyEmails.length === 0) {
        setNotifyEmailError('Please add at least one email address');
        return;
      }
      const invalidList = notifyEmails.filter((e) => !isValidNotifyEmail(e));
      if (invalidList.length > 0) {
        setNotifyEmailError(`Invalid email addresses: ${invalidList.join(', ')}`);
        return;
      }
    }
    setNotifyEmailError('');

    const numeric = (key: NumericFieldKey): number => Number(formState[key] || 0);
    const numOpt = (key: NumericFieldKey): number | undefined => {
      const s = formState[key] as string;
      if (s == null || String(s).trim() === '') return undefined;
      const n = Number(s);
      return Number.isNaN(n) ? undefined : n;
    };

    const nominalPipeLengthFtCanonical = numOpt('nominalPipeLengthFt') != null
      ? convertLengthToFt(Number(formState.nominalPipeLengthFt), nominalPipeLengthUnit)
      : undefined;
    const targetPipelineLengthFtCanonical = numOpt('targetPipelineLengthFt') != null
      ? convertLengthToFt(Number(formState.targetPipelineLengthFt), targetPipelineLengthUnit)
      : undefined;
    const nominalODMmCanonical = numOpt('nominalODMm');
    /** Setup/DB HiLo is mm-only — no unit conversion. */
    const hiloThresholdMmCanonical = numOpt('hiloThresholdMm');

    const exclusion6OClockDeg = numOpt('exclusion6OClockMm');
    const weldSeamExclusionDeg = numOpt('weldSeamExclusionMm');

    const config: PipeBasedGenerateSubmitValues['config'] = {
      targetNumberOfPipes: numeric('targetNumberOfPipes'),
    };
    if (nominalPipeLengthFtCanonical != null) config.nominalPipeLengthFt = nominalPipeLengthFtCanonical;
    if (targetPipelineLengthFtCanonical != null) config.targetPipelineLengthFt = targetPipelineLengthFtCanonical;
    if (nominalODMmCanonical != null) config.nominalODmm = nominalODMmCanonical;
    if (exclusion6OClockDeg != null) config.exclusion6OClockMm = exclusion6OClockDeg;
    if (weldSeamExclusionDeg != null) config.weldSeamExclusionMm = weldSeamExclusionDeg;
    if (hiloThresholdMmCanonical != null) config.hiLoThresholdMm = hiloThresholdMmCanonical;
    if (formState.pipeMaterial?.trim()) config.pipeMaterial = formState.pipeMaterial.trim();
    config.pipeType = formState.pipeType === 'single' ? 'Single' : 'Double';
    config.pipeRotation =
      formState.pipeRotation === 'any'
        ? 'Any'
        : formState.pipeRotation === 'clockwise'
          ? 'Clockwise'
          : 'AntiClockwise';
    config.fitQuality = formState.fitQuality === 'optimal' ? 'Optimal' : 'Acceptable';
    config.allowPipeTranslation = formState.allowPipeTranslation;

    const payload: PipeBasedGenerateSubmitValues = {
      mode: formState.mode,
      sequenceName,
      config,
    };
    if (formState.mode !== 'new' && existingVal) {
      payload.existingSequenceRef = parseExistingSequenceRef(existingVal);
    }

    if (notifyByEmail && notifyEmails.length > 0) {
      payload.notifyEmails = [...notifyEmails];
      payload.notifyEmail = notifyEmails[0];
    }

    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          dialogClassName ??
          'w-screen h-screen max-w-none max-h-[100dvh] m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden flex flex-col'
        }
      >
        <div className="flex flex-col h-full min-h-0 overflow-hidden flex-1">
          <DialogHeader className="px-4 sm:px-6 py-3 border-b flex flex-row items-start justify-between gap-3 bg-white shrink-0">
            <div className="flex items-start gap-4 min-w-0">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                variant="outline"
                disabled={isSubmitting}
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-lg font-bold text-gray-900 truncate pr-2" title={title}>
                  {title}
                </DialogTitle>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {metricsPills.map((item) => (
                    <span key={item.label} className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}>
                      {item.label}: {item.text}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <div
              className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pb-3 ${isUpdateLayout ? 'xl:grid xl:grid-cols-[minmax(0,35%)_minmax(0,65%)] xl:gap-4 xl:pt-3 xl:items-stretch' : ''}`}
            >
            {isUpdateLayout && onSelectedPipeIdsChange && (
              <section className="space-y-4 min-h-0 flex flex-col border rounded-lg p-4 bg-white xl:max-h-full">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Select Pipes</h3>
                  <span className="text-sm text-gray-600">{(selectedPipeIds ?? []).length} selected</span>
                </div>
                {pipeSelectionToolbar}
                <div className={`grid grid-cols-1 ${simplePipeSelectionTable ? 'md:grid-cols-1' : 'md:grid-cols-3'} gap-3`}>
                  <Input
                    value={pipeSearchText}
                    onChange={(e) => setPipeSearchText(e.target.value)}
                    placeholder="Search by Pipe ID"
                    className={simplePipeSelectionTable ? '' : 'md:col-span-2'}
                  />
                  {!simplePipeSelectionTable && (
                    <Select value={pipeStatusFilter} onValueChange={setPipeStatusFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All status</SelectItem>
                        {uniquePipeStatuses.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {!simplePipeSelectionTable && (
                  <div className="grid grid-cols-1 gap-3">
                    <Select value={pipeSequenceFilter} onValueChange={setPipeSequenceFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sequence" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sequence</SelectItem>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {uniquePipeSequences.map((sequence) => (
                          <SelectItem key={sequence} value={sequence}>
                            {sequence}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="border rounded-md overflow-hidden min-h-0 flex-1 flex flex-col">
                  <div className="min-h-0 flex-1 max-h-[min(520px,45vh)] xl:max-h-none overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 z-10">
                        <tr className="border-b">
                          <th className="text-left px-3 py-2 w-12">
                            <Checkbox
                              checked={
                                filteredPipeSelectionItems.length > 0 &&
                                filteredPipeSelectionItems
                                  .filter((item) => !item.disabled)
                                  .every((item) => (selectedPipeIds ?? []).includes(item.id))
                              }
                              onCheckedChange={(checked) => {
                                const selectableIds = filteredPipeSelectionItems
                                  .filter((item) => !item.disabled)
                                  .map((item) => item.id);
                                if (!checked) {
                                  onSelectedPipeIdsChange(
                                    (selectedPipeIds ?? []).filter((id) => !selectableIds.includes(id))
                                  );
                                  return;
                                }
                                onSelectedPipeIdsChange(
                                  Array.from(new Set([...(selectedPipeIds ?? []), ...selectableIds]))
                                );
                              }}
                            />
                          </th>
                          <th className="text-left px-3 py-2">Sl No</th>
                          <th className="text-left px-3 py-2">Pipe ID</th>
                          {!simplePipeSelectionTable && (
                            <>
                              <th className="text-left px-3 py-2">Sequence</th>
                              <th className="text-left px-3 py-2">Status</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPipeSelectionItems.map((pipe, idx) => {
                          const checked = (selectedPipeIds ?? []).includes(pipe.id);
                          const rowDisabled = Boolean(pipe.disabled);
                          const row = (
                            <tr
                              key={pipe.id}
                              className={`border-b ${rowDisabled ? 'bg-gray-50 text-gray-400' : 'hover:bg-gray-50'}`}
                            >
                              <td className="px-3 py-2">
                                {rowDisabled ? (
                                  <Tooltip content={pipe.disabledReason ?? 'This pipe has already been used for sequence'}>
                                    <span className="inline-flex">
                                      <Checkbox checked={checked} disabled />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(isChecked) => {
                                      const current = selectedPipeIds ?? [];
                                      if (isChecked) {
                                        if (!current.includes(pipe.id)) onSelectedPipeIdsChange([...current, pipe.id]);
                                      } else {
                                        onSelectedPipeIdsChange(current.filter((id) => id !== pipe.id));
                                      }
                                    }}
                                  />
                                )}
                              </td>
                              <td className="px-3 py-2">{idx + 1}</td>
                              <td className="px-3 py-2 font-medium">{pipe.label}</td>
                              {!simplePipeSelectionTable && (
                                <>
                                  <td className="px-3 py-2">{pipe.sequence ?? 'Unassigned'}</td>
                                  <td className="px-3 py-2">{pipe.status ?? 'Unknown'}</td>
                                </>
                              )}
                            </tr>
                          );
                          return row;
                        })}
                        {filteredPipeSelectionItems.length === 0 && (
                          <tr>
                            <td colSpan={simplePipeSelectionTable ? 3 : 5} className="px-3 py-8 text-center text-gray-500">
                              No pipes found for current filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            <div className="min-h-0 min-w-0">
          <div className="pt-2 pb-3">
            <p className="text-sm text-gray-600">
              Configure how the sequence should be generated for the selected pipes.
              {selectedPipeCount != null && selectedPipeCount > 0 && (
                <span className="ml-1 font-medium text-gray-700">
                  ({selectedPipeCount} pipe{selectedPipeCount !== 1 ? 's' : ''} selected)
                </span>
              )}
              {pipeIds.length === 0 && (
                <span className="ml-1 text-amber-600 font-medium">Select at least one pipe in the Pipe Details table.</span>
              )}
            </p>
          </div>

          {sequenceModeOptions !== 'append-only' && (
          <section className="space-y-3 mb-4">
            {sequenceModeOptions !== 'none' && (
              <>
                <h3 className="text-base font-semibold text-gray-900">Sequence Type</h3>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col lg:flex-row gap-3 lg:items-stretch">
                    {(() => {
                      const newDisabled = pipeIds.length < 3;
                      const labelEl = (
                        <label
                          className={`flex flex-1 min-w-0 items-start gap-3 rounded-lg border px-3 py-2.5 ${
                            newDisabled
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-75'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                          }`}
                        >
                          <input
                            type="radio"
                            name="sequenceType"
                            value="new"
                            checked={sequenceType === 'new'}
                            disabled={newDisabled || lockModeSelection}
                            onChange={() => {
                              if (newDisabled || lockModeSelection) return;
                              setSequenceType('new');
                              setFormState((prev) => ({ ...prev, mode: 'new' }));
                            }}
                            className="mt-1 h-4 w-4 text-primary border-gray-300 shrink-0 disabled:cursor-not-allowed"
                          />
                          <span className="block min-w-0 text-left">
                            <span className="block text-sm font-medium text-gray-900">Generate New Sequence</span>
                            <span className="block text-xs text-gray-500 mt-0.5 leading-snug">
                              Create a fresh sequence using the parameters below.
                            </span>
                          </span>
                        </label>
                      );
                      return newDisabled ? (
                        <Tooltip content="Select at least 3 pipes to create a fresh sequence.">{labelEl}</Tooltip>
                      ) : (
                        labelEl
                      );
                    })()}

                    <label className="flex flex-1 min-w-0 items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:border-gray-300 cursor-pointer">
                      <input
                        type="radio"
                        name="sequenceType"
                        value="regenerate"
                        checked={sequenceType === 'regenerate'}
                        disabled={lockModeSelection}
                        onChange={() => {
                          if (lockModeSelection) return;
                          setSequenceType('regenerate');
                          setFormState((prev) => ({
                            ...prev,
                            mode: 'regenerate',
                          }));
                        }}
                        className="mt-1 h-4 w-4 text-primary border-gray-300 shrink-0"
                      />
                      <span className="block min-w-0 text-left">
                        <span className="block text-sm font-medium text-gray-900">Regenerate Full Sequence</span>
                        <span className="block text-xs text-gray-500 mt-0.5 leading-snug">
                          Recreate the entire sequence from scratch
                        </span>
                      </span>
                    </label>
                  </div>

                  {sequenceModeOptions === 'all' &&
                    (() => {
                      const appendDisabled = pipeIds.length < 1;
                      const appendLabelEl = (
                        <label
                          className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
                            appendDisabled
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-75'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                          }`}
                        >
                          <input
                            type="radio"
                            name="sequenceType"
                            value="append"
                            checked={sequenceType === 'append'}
                            disabled={appendDisabled || lockModeSelection}
                            onChange={() => {
                              if (appendDisabled || lockModeSelection) return;
                              setSequenceType('append');
                              setFormState((prev) => ({ ...prev, mode: 'append' }));
                            }}
                            className="mt-1 h-4 w-4 text-primary border-gray-300 disabled:cursor-not-allowed"
                          />
                          <span className="block text-left">
                            <span className="block text-sm font-medium text-gray-900">Append to Existing Sequence</span>
                            <span className="block text-xs text-gray-500 mt-1">
                              Keep current progress and add new pipes to the end
                            </span>
                          </span>
                        </label>
                      );
                      return appendDisabled ? (
                        <Tooltip content="Select at least 1 pipe to append to existing sequence.">{appendLabelEl}</Tooltip>
                      ) : (
                        appendLabelEl
                      );
                    })()}
                </div>
              </>
            )}

            {sequenceModeOptions === 'none' && sequenceType !== 'new' && (
              <p className="text-sm text-gray-600">Selected pipes will be appended to this sequence.</p>
            )}

            {sequenceType === 'new' && (
              <div className="mt-4 max-w-md space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">
                  Sequence Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formState.sequenceName ?? ''}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      sequenceName: e.target.value,
                    }))
                  }
                  disabled={isSubmitting}
                  placeholder="e.g. Run_2026_03_06"
                />
                {sequenceError === 'Sequence name is required' && (
                  <p className="text-xs text-red-600 mt-1">{sequenceError}</p>
                )}
              </div>
            )}

            {sequenceType !== 'new' && (
              <div className="mt-4 max-w-md">
                <Label className="text-sm font-medium text-gray-700">
                  Select existing sequence <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formState.existingSequenceValue ?? ''}
                  onValueChange={(value) => {
                    const option = sequences.find((s) => s.value === value);
                    setFormState((prev) => ({
                      ...prev,
                      existingSequenceValue: value,
                      sequenceName: option?.sequenceName ?? prev.sequenceName,
                    }));
                  }}
                  disabled={sequences.length === 0 || isSubmitting}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue
                      placeholder={
                        sequences.length === 0 ? 'No sequences available' : 'Select Sequence'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sequences.map((seq) => (
                      <SelectItem key={seq.value} value={seq.value}>
                        {seq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {sequences.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1.5">No sequences available.</p>
                )}
                {sequenceError === 'Select an existing sequence' && (
                  <p className="text-xs text-red-600 mt-1.5">{sequenceError}</p>
                )}
              </div>
            )}
          </section>
          )}

          <div className="border-t border-gray-200 my-4" />

          <section className="space-y-4 mb-4">
            {isAutofillLoading && (
              <div className="text-sm text-gray-500 mb-2">
                Loading project configuration for selected pipe size…
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-4">
              {/*
                Nominal pipe length, target pipe count, pipeline length, and nominal OD are shown in header pills only.
                Inputs commented out to avoid duplicate UI; values still drive submit via formState.

              <div className="space-y-1.5"> ... nominal length ... </div>
              <div className="space-y-1.5"> ... target pipes ... </div>
              <div className="space-y-1.5"> ... pipeline length ... </div>
              <div className="space-y-1.5"> ... nominal OD ... </div>
              */}
              {(errors.nominalPipeLengthFt ||
                errors.targetNumberOfPipes ||
                errors.targetPipelineLengthFt ||
                errors.nominalODMm) && (
                <div className="col-span-full space-y-0.5 text-xs text-red-600">
                  {errors.nominalPipeLengthFt && <p>{errors.nominalPipeLengthFt}</p>}
                  {errors.targetNumberOfPipes && <p>{errors.targetNumberOfPipes}</p>}
                  {errors.targetPipelineLengthFt && <p>{errors.targetPipelineLengthFt}</p>}
                  {errors.nominalODMm && <p>{errors.nominalODMm}</p>}
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-gray-700">
                    6 O&apos;Clock Exclusion (deg)
                  </Label>
                  <Tooltip content={FIELD_TOOLTIPS.exclusion6OClockMm}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <Input
                  id="exclusion6OClockMm"
                  type="number"
                  min={0}
                  max={90}
                  step={1}
                  value={formState.exclusion6OClockMm}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Block negatives / dash; keep empty; clamp to 0–90
                    if (v === '' || v === '-') {
                      handleNumericChange('exclusion6OClockMm', '');
                      return;
                    }
                    const numeric = Number(v);
                    if (Number.isNaN(numeric)) return;
                    const clamped = Math.min(90, Math.max(0, numeric));
                    handleNumericChange('exclusion6OClockMm', String(clamped));
                  }}
                  disabled={isAutofillLoading || isSubmitting}
                  className="disabled:opacity-70 disabled:cursor-not-allowed"
                  placeholder="e.g. 60"
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.exclusion6OClockMm && (
                  <p className="text-xs text-red-600 mt-1">{errors.exclusion6OClockMm}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-gray-700">
                    Weld Seam Exclusion (deg)
                  </Label>
                  <Tooltip content={FIELD_TOOLTIPS.weldSeamExclusionMm}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <Input
                  type="number"
                  value={formState.weldSeamExclusionMm}
                  onChange={(e) => handleNumericChange('weldSeamExclusionMm', e.target.value)}
                  disabled={isAutofillLoading || isSubmitting}
                  className="disabled:opacity-70 disabled:cursor-not-allowed"
                />
                {errors.weldSeamExclusionMm && (
                  <p className="text-xs text-red-600 mt-1">{errors.weldSeamExclusionMm}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-gray-700">
                    HiLo Threshold <span className="text-red-500">*</span>
                  </Label>
                  <Tooltip content={FIELD_TOOLTIPS.hiloThresholdMm}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <div className="flex w-full rounded-md border border-gray-200 overflow-hidden">
                  <Input
                    type="number"
                    step="0.01"
                    value={formState.hiloThresholdMm}
                    onChange={(e) => handleNumericChange('hiloThresholdMm', e.target.value)}
                    disabled={isAutofillLoading || isSubmitting}
                    className="flex-[0_0_70%] rounded-r-none border-0 border-r border-gray-200 rounded-l-md focus-visible:ring-inset disabled:opacity-70 disabled:cursor-not-allowed"
                  />
                  <Select value="mm" disabled>
                    <SelectTrigger className="flex-[0_0_30%] h-10 rounded-l-none rounded-r-md border-0 border-l border-gray-200 disabled:opacity-100">
                      <SelectValue>{MM_UNIT_LABELS.mm}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mm">{MM_UNIT_LABELS.mm}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {errors.hiloThresholdMm && (
                  <p className="text-xs text-red-600 mt-1">{errors.hiloThresholdMm}</p>
                )}
              </div>
            </div>
          </section>

          <div className="border-t border-gray-200 my-4" />

          <section className="space-y-4 pb-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-gray-700">Pipe Material</Label>
                  <Tooltip content={FIELD_TOOLTIPS.pipeMaterial}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <Input
                  type="text"
                  value={formState.pipeMaterial}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      pipeMaterial: e.target.value,
                    }))
                  }
                  disabled={isSubmitting}
                  className="disabled:opacity-70 disabled:cursor-not-allowed"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-gray-700">Pipe Type</Label>
                  <Tooltip content={FIELD_TOOLTIPS.pipeType}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <Select
                  value={formState.pipeType}
                  onValueChange={(value: 'single' | 'double') =>
                    setFormState((prev) => ({
                      ...prev,
                      pipeType: value,
                    }))
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="double">Double</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-gray-700">Pipe Rotation</Label>
                  <Tooltip content={FIELD_TOOLTIPS.pipeRotation}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <Select
                  value={formState.pipeRotation}
                  onValueChange={(value: 'any' | 'clockwise' | 'antiClockwise') =>
                    setFormState((prev) => ({
                      ...prev,
                      pipeRotation: value,
                    }))
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="clockwise">Clockwise</SelectItem>
                    <SelectItem value="antiClockwise">Anti Clockwise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium text-gray-700">Fit Quality</Label>
                  <Tooltip content={FIELD_TOOLTIPS.fitQuality}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                </div>
                <Select
                  value={formState.fitQuality}
                  onValueChange={(value: 'optimal' | 'acceptable') =>
                    setFormState((prev) => ({
                      ...prev,
                      fitQuality: value,
                    }))
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="optimal">Optimal - Use the best rotation that works</SelectItem>
                    <SelectItem value="acceptable">Acceptable - Use the first rotation that works</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 rounded-lg border border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Label htmlFor="allow-pipe-translation" className="text-sm font-medium text-gray-700">
                  Allow Pipe Translation
                </Label>
                <Tooltip content={FIELD_TOOLTIPS.allowPipeTranslation}>
                  <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                </Tooltip>
                <Switch
                  id="allow-pipe-translation"
                  checked={formState.allowPipeTranslation}
                  onCheckedChange={(checked: boolean) =>
                    setFormState((prev) => ({
                      ...prev,
                      allowPipeTranslation: checked,
                    }))
                  }
                  disabled={isSubmitting}
                />
              </div>
              <p className="text-xs text-gray-500">
                When enabled, pipes can be translated along the axis to improve fit quality.
              </p>
            </div>

            <CompletionEmailNotifyBlock
            disabled={isSubmitting}
            notifyEnabled={notifyByEmail}
            onNotifyEnabledChange={(checked) => {
              setNotifyByEmail(checked);
              setNotifyEmailError('');
            }}
            emails={notifyEmails}
            newEmail={newNotifyEmail}
            onNewEmailChange={(v) => {
              setNewNotifyEmail(v);
              if (notifyEmailError) setNotifyEmailError('');
            }}
            emailError={notifyEmailError}
            onAddEmail={handleAddNotifyEmail}
            onRemoveEmail={handleRemoveNotifyEmail}
            onNewEmailKeyDown={handleNotifyKeyDown}
          />
          </section>
            </div>
            </div>
          </div>

        <div className="flex flex-col gap-3 px-4 py-3 border-t bg-background shrink-0">
          {/* <div className="space-y-1">
            <p className="text-sm font-medium text-gray-900">Email when sequence is ready</p>
            <p className="text-xs text-gray-500">
              Optional — recipients below get the report when generation completes (shown in the modal footer, below the form).
            </p>
          </div> */}
          
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid}
              className="bg-hero-red text-white hover:bg-hero-red-60 min-w-[120px]"
            >
              {isSubmitting ? 'Generating...' : submitLabel}
            </Button>
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

